/**
 * GoldenCalc — Core Staircase Configuration Models
 *
 * All dimensions in millimeters unless noted otherwise.
 */
import { WOOD_PRICES_USD } from '../const/wood-prices.const';

// ─── Stair Types ───────────────────────────────────────────────

export type IStairType = 'straight' | 'l-shape' | 'u-shape' | 'spiral';
export type ITurnDirection = 'left' | 'right';
export type ITurnType = 'landing' | 'winder';
export type IStringerType = 'tetiva' | 'kosour';
export type IRailingStyle = 'square' | 'turned' | 'flat' | 'glass';

// ─── Materials ─────────────────────────────────────────────────

export interface IWoodMaterial {
    id: string;
    name: string;
    density: number; // kg/m³
    pricePerM3: number; // USD
    textureUrl: string;
    normalMapUrl: string;
    roughness: number; // 0–1 for PBR
    color: string; // hex fallback
}

export const WOOD_MATERIALS: IWoodMaterial[] = [
    {
        id: 'pine',
        name: $localize`:@@woodPine:Сосна`,
        density: 520,
        pricePerM3: WOOD_PRICES_USD.pine,
        textureUrl: '',
        normalMapUrl: '',
        roughness: 0.7,
        color: '#D4A76A',
    },
    {
        id: 'birch',
        name: $localize`:@@woodBirch:Берёза`,
        density: 650,
        pricePerM3: WOOD_PRICES_USD.birch,
        textureUrl: '',
        normalMapUrl: '',
        roughness: 0.6,
        color: '#E8D5B7',
    },
    {
        id: 'larch',
        name: $localize`:@@woodLarch:Лиственница`,
        density: 660,
        pricePerM3: WOOD_PRICES_USD.larch,
        textureUrl: '',
        normalMapUrl: '',
        roughness: 0.65,
        color: '#C49A6C',
    },
    {
        id: 'beech',
        name: $localize`:@@woodBeech:Бук`,
        density: 720,
        pricePerM3: WOOD_PRICES_USD.beech,
        textureUrl: '',
        normalMapUrl: '',
        roughness: 0.55,
        color: '#D4A574',
    },
    {
        id: 'oak',
        name: $localize`:@@woodOak:Дуб`,
        density: 750,
        pricePerM3: WOOD_PRICES_USD.oak,
        textureUrl: '',
        normalMapUrl: '',
        roughness: 0.5,
        color: '#B8860B',
    },
    {
        id: 'ash',
        name: $localize`:@@woodAsh:Ясень`,
        density: 690,
        pricePerM3: WOOD_PRICES_USD.ash,
        textureUrl: '',
        normalMapUrl: '',
        roughness: 0.55,
        color: '#C8B78C',
    },
];

// ─── Railing Config ────────────────────────────────────────────

export interface IRailingConfig {
    enabled: boolean;
    style: IRailingStyle;
    height: number; // mm, default 900
    balusterWidth: number; // mm, default 30
    balusterSpacing: number; // mm, default 130
    handrailWidth: number; // mm, default 50
    handrailHeight: number; // mm, default 40
    postWidth: number; // mm, default 60
    upperEnabled: boolean; // upper floor railing around opening
    sides: 'left' | 'right' | 'both';
}

export const DEFAULT_RAILING: IRailingConfig = {
    enabled: true,
    style: 'square',
    height: 1000,
    balusterWidth: 40,
    balusterSpacing: 130,
    handrailWidth: 70,
    handrailHeight: 40,
    postWidth: 80,
    upperEnabled: true,
    sides: 'both',
};

// ─── Main Stair Config ────────────────────────────────────────

export interface IStairConfig {
    // Type
    type: IStairType;
    turnDirection: ITurnDirection;
    turnType: ITurnType;

    // Room dimensions
    roomLength: number; // mm — room length along Z
    roomWidth: number; // mm — room width along X
    floorHeight: number; // mm — floor-to-floor
    floorThickness: number; // mm — upper floor slab thickness

    // Opening position & size
    openingLength: number; // mm — stairwell opening along Z
    openingWidth: number; // mm — stairwell opening along X
    openingX: number; // mm — opening position X (from room corner)
    openingZ: number; // mm — opening position Z (from room corner)
    openingLocked: boolean; // lock opening position (disable drag)

    // Stair dimensions
    stairWidth: number; // mm — march width
    stringerType: IStringerType;
    risersEnabled: boolean; // false = open stair: no riser boards anywhere

    // Steps (auto-calculated or manual)
    riserHeight: number | null; // mm — null = auto
    treadDepth: number | null; // mm — null = auto
    treadThickness: number; // mm, default 40
    noseLength: number; // mm, default 30
    stepCount: number | null; // null = auto
    stairAngle: number | null; // degrees — null = auto; overrides treadDepth when set

    // Materials
    treadMaterialId: string;
    riserMaterialId: string;
    stringerMaterialId: string;
    handrailMaterialId: string; // handrail runs, the upper-floor railing's included
    balusterMaterialId: string; // balusters and posts, the upper-floor railing's included

    // Railing
    railing: IRailingConfig;

    // Turn-specific (L-shape / U-shape)
    turnAtStep: number | null; // null = auto (split evenly); step index where turn begins

    // U-shape specific
    flightGap: number; // mm — gap between parallel flights

    // Spiral-specific
    spiralDiameter: number | null; // mm
    spiralRotation: number | null; // degrees
}

// ─── UI Constraints ───────────────────────────────────────────

interface IFieldConstraint {
    readonly min: number;
    readonly max: number;
    readonly step: number;
}

interface IRoomConstraints {
    readonly length: IFieldConstraint;
    readonly width: IFieldConstraint;
    readonly floorHeight: IFieldConstraint;
    readonly floorThickness: IFieldConstraint;
}

interface IOpeningConstraints {
    readonly length: IFieldConstraint;
    readonly width: IFieldConstraint;
    readonly positionStep: number;
}

interface IStairConstraints {
    readonly width: IFieldConstraint;
    readonly treadThickness: IFieldConstraint;
    readonly noseLength: IFieldConstraint;
    readonly angle: IFieldConstraint;
    readonly angleLUShape: IFieldConstraint;
}

interface IRailingConstraints {
    readonly height: IFieldConstraint;
    readonly handrailWidth: IFieldConstraint;
    readonly balusterWidth: IFieldConstraint;
    readonly balusterSpacing: IFieldConstraint;
    readonly postWidth: IFieldConstraint;
}

interface ISpiralConstraints {
    readonly diameter: IFieldConstraint;
    readonly openingMargin: number;
    readonly defaultDiameter: number;
    readonly floorHeightMin: number;
}

interface ILShapeConstraints {
    readonly winderStepCount: number;
    readonly defaultTurnAtStep: Readonly<Record<ITurnType, number>>;
}

interface IUShapeConstraints {
    readonly winderStepCount: number;
    readonly flightGap: IFieldConstraint;
    readonly defaultTurnAtStep: Readonly<Record<ITurnType, number>>;
}

interface IBlondelEstimate {
    readonly idealRiserHeight: number;
    readonly treadDepthMin: number;
    readonly treadDepthMax: number;
    readonly blondelIdeal: number;
    readonly minRemainingSteps: number;
    readonly minFlightRatio: number;
}

export interface IConfigConstraints {
    readonly room: IRoomConstraints;
    readonly opening: IOpeningConstraints;
    readonly stair: IStairConstraints;
    readonly railing: IRailingConstraints;
    readonly spiral: ISpiralConstraints;
    readonly lShape: ILShapeConstraints;
    readonly uShape: IUShapeConstraints;
    readonly blondel: IBlondelEstimate;
}

export const CONFIG_CONSTRAINTS: IConfigConstraints = {
    room: {
        length: { min: 1000, max: 20000, step: 100 },
        width: { min: 1000, max: 20000, step: 100 },
        floorHeight: { min: 1000, max: 6000, step: 10 },
        floorThickness: { min: 100, max: 1200, step: 10 },
    },
    opening: {
        length: { min: 1000, max: 6000, step: 10 },
        width: { min: 500, max: 6000, step: 10 },
        positionStep: 10,
    },
    stair: {
        width: { min: 600, max: 1500, step: 10 },
        treadThickness: { min: 30, max: 250, step: 5 },
        noseLength: { min: 0, max: 70, step: 5 },
        angle: { min: 30, max: 70, step: 1 }, // straight stair (no opening attachment constraint)
        angleLUShape: { min: 30, max: 39, step: 1 }, // L/U-shape: per СП 54.13330 (former СНиП 2.08.01-89) norms
    },
    railing: {
        height: { min: 600, max: 6000, step: 10 },
        handrailWidth: { min: 40, max: 300, step: 5 },
        balusterWidth: { min: 20, max: 150, step: 5 },
        balusterSpacing: { min: 0, max: 600, step: 10 },
        postWidth: { min: 40, max: 350, step: 5 },
    },
    spiral: {
        diameter: { min: 1600, max: 3000, step: 50 },
        openingMargin: 100,
        defaultDiameter: 2600,
        // No default rotation: the spiral calculator picks the riser count and the step angle
        // for the floor at hand, and the rotation follows from them. A fixed 330° kept the
        // turn while the riser count changed with the floor: at 3000 mm the walking-line tread
        // fell to 243 mm, at 2500 mm the Blondel value rose to 654.
        floorHeightMin: 2200,
    },
    lShape: {
        winderStepCount: 3,
        // With the default opening, flight 2 is forced to 6 treads: turning at 9 on a
        // landing gives 17 risers of 176.5 mm and Blondel 620, inside the norm. Turning
        // earlier raises the riser past it — at 7 the default opened with a Blondel error.
        // Winders take two more risers than a landing, so they turn two treads earlier for
        // the same 17 risers; turning at 9 on winders gave 19 × 158 mm and Blondel 582.
        defaultTurnAtStep: { landing: 9, winder: 7 },
    },
    uShape: {
        winderStepCount: 6,
        flightGap: { min: 0, max: 4000, step: 10 },
        // With the default 3700 mm opening, flight 2 takes 10 treads on a landing, so
        // 5 + 1 + 1 + 10 = 17 risers of 176.5 mm and Blondel 633; turning at 6 gave Blondel 603,
        // in the warning band. Winders (six of them, and a deeper turn zone) leave flight 2 nine
        // treads: 1 + 1 + 6 + 9 = 17 risers and Blondel 627; turning at 2 gave 18 risers. Both
        // are the auto layout's own pick and open with no warning in the norm check.
        defaultTurnAtStep: { landing: 5, winder: 1 },
    },
    blondel: {
        idealRiserHeight: 175,
        treadDepthMin: 250,
        treadDepthMax: 300,
        blondelIdeal: 630,
        minRemainingSteps: 2,
        minFlightRatio: 0.2,
    },
};

// ─── Default Config ───────────────────────────────────────────

/**
 * The species of a fresh config: treads and balusters with posts in birch, risers, the frame
 * and the handrail in oak. A config without the railing species still takes the treads'
 * species for them (`railingMaterialsOf`), not these defaults.
 */
const DEFAULT_TREAD_MATERIAL_ID: string = 'birch';
const DEFAULT_RISER_MATERIAL_ID: string = 'oak';
const DEFAULT_STRINGER_MATERIAL_ID: string = 'oak';
const DEFAULT_HANDRAIL_MATERIAL_ID: string = 'oak';
const DEFAULT_BALUSTER_MATERIAL_ID: string = 'birch';

export const DEFAULT_STAIR_CONFIG: IStairConfig = {
    type: 'straight',
    turnDirection: 'right',
    turnType: 'landing',

    roomLength: 6500,
    roomWidth: 6500,
    floorHeight: 2800,
    floorThickness: 200,

    // Headroom is measured from the nosing line. A 3400 mm opening left the straight stair
    // 1989 mm under the slab edge, below the 2000 minimum; 3600 mm gives 2118 mm, 3700 mm
    // gives 2183 mm, clear of the warning band. The opening still ends at the far wall, so
    // the stair starts where it did.
    openingLength: 3700,
    openingWidth: 2530,
    openingX: 0,
    openingZ: 2800,
    openingLocked: false,

    stairWidth: 900,
    stringerType: 'kosour',
    risersEnabled: true,

    riserHeight: null,
    treadDepth: null,
    treadThickness: 40,
    noseLength: 30,
    stepCount: null,
    stairAngle: 33,

    treadMaterialId: DEFAULT_TREAD_MATERIAL_ID,
    riserMaterialId: DEFAULT_RISER_MATERIAL_ID,
    stringerMaterialId: DEFAULT_STRINGER_MATERIAL_ID,
    handrailMaterialId: DEFAULT_HANDRAIL_MATERIAL_ID,
    balusterMaterialId: DEFAULT_BALUSTER_MATERIAL_ID,

    railing: { ...DEFAULT_RAILING },

    turnAtStep: null,

    flightGap: 660,

    spiralDiameter: null,
    spiralRotation: null,
};

/**
 * The step a turning stair turns at when the field is untouched, per turn type: winders
 * take more risers than a landing, so they turn earlier for the same in-norm step count.
 * Straight and spiral stairs have no turn, so they clear it. One source for the initial
 * config, the type switch and the turn-type switch: two copies of this number drifted
 * apart and opened the L-shape out of norm.
 */
export function defaultTurnAtStep(type: IStairType, turnType: ITurnType = DEFAULT_STAIR_CONFIG.turnType): number | null {
    if (type === 'l-shape') {
        return CONFIG_CONSTRAINTS.lShape.defaultTurnAtStep[turnType];
    }
    if (type === 'u-shape') {
        return CONFIG_CONSTRAINTS.uShape.defaultTurnAtStep[turnType];
    }
    return null;
}

export function buildInitialConfig(type: IStairType): IStairConfig {
    const base: IStairConfig = {
        ...DEFAULT_STAIR_CONFIG,
        type,
        turnAtStep: defaultTurnAtStep(type, DEFAULT_STAIR_CONFIG.turnType),
        railing: { ...DEFAULT_STAIR_CONFIG.railing },
    };
    if (type !== 'spiral') {
        return base;
    }
    const sp: typeof CONFIG_CONSTRAINTS.spiral = CONFIG_CONSTRAINTS.spiral;
    const size: number = sp.defaultDiameter + sp.openingMargin;
    return {
        ...base,
        spiralDiameter: sp.defaultDiameter,
        spiralRotation: null,
        openingLength: size,
        openingWidth: size,
        openingX: 0,
        openingZ: Math.max(0, base.roomLength - size),
    };
}

/**
 * The config a stair-type switch starts from: every per-type default comes from
 * `buildInitialConfig(type)` — turn type and turn step included — so a switch lands on
 * the same in-norm calculation as opening that type fresh. Only what the user set for
 * the room, the opening, the stair body and the materials carries over. A turn type
 * hard-coded in the switch once made the L-shape open on winders with the landing's turn
 * step: 19 × 158 mm and a Blondel error.
 */
export function buildTypeSwitchConfig(current: IStairConfig, type: IStairType): IStairConfig {
    return {
        ...buildInitialConfig(type),
        roomLength: current.roomLength,
        roomWidth: current.roomWidth,
        floorHeight: current.floorHeight,
        floorThickness: current.floorThickness,
        openingLength: current.openingLength,
        openingWidth: current.openingWidth,
        openingX: current.openingX,
        openingZ: current.openingZ,
        openingLocked: current.openingLocked,
        spiralDiameter: current.spiralDiameter,
        spiralRotation: current.spiralRotation,
        stairWidth: current.stairWidth,
        stairAngle: current.stairAngle,
        treadThickness: current.treadThickness,
        noseLength: current.noseLength,
        treadMaterialId: current.treadMaterialId,
        risersEnabled: current.risersEnabled,
        riserMaterialId: current.riserMaterialId,
        stringerMaterialId: current.stringerMaterialId,
        handrailMaterialId: current.handrailMaterialId,
        balusterMaterialId: current.balusterMaterialId,
        turnDirection: current.turnDirection,
        railing: { ...current.railing },
    };
}

/**
 * Whether a config draws risers. A link, a saved file or a stored session made before the riser
 * toggle carries no flag, and every stair was drawn with risers then.
 */
export function risersEnabledOf(source: Partial<IStairConfig>): boolean {
    return source.risersEnabled ?? true;
}

/** The two railing species of a config: the handrail and the balusters with posts. */
export type IRailingMaterialIds = Pick<IStairConfig, 'handrailMaterialId' | 'balusterMaterialId'>;

/**
 * The railing species a config carries, or the treads' species for one that does not carry
 * them: a link, a saved file or a stored session made before the railing had material groups
 * of its own, when the whole railing took the treads' wood. A species that is present stays,
 * so once chosen the railing no longer follows the treads.
 */
export function railingMaterialsOf(source: Partial<IStairConfig>, treadMaterialId: string): IRailingMaterialIds {
    return {
        handrailMaterialId: typeof source.handrailMaterialId === 'string' ? source.handrailMaterialId : treadMaterialId,
        balusterMaterialId: typeof source.balusterMaterialId === 'string' ? source.balusterMaterialId : treadMaterialId,
    };
}
